import { Stack, Box, Divider, useTheme } from "@mui/material";
import { useParams } from "react-router-dom";
import PropertyTrackTabs from "../tabs";
import { useTrackQuery } from "@/modules/property-tracks/hooks/track.hook";
import WithErrorHandling from "@/modules/common/components/wrappers/with-error-handling";
import { getJourneyByOldId } from "@/modules/lead-page/api/lead-page.v2.api";
import { useQuery } from "@tanstack/react-query";
import useLeadPageDataFetching from "@/modules/lead-page/hooks/lead-page-data-fetching.hook";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import CustomerProfileSkeleton from "@/modules/lead-page/new/components/sidebar/skeletons/customer-profile-skeleton";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import LeadSidebar from "@/modules/lead-page/new/components/sidebar/lead-sidebar";
import { useState } from "react";
import LoadingSkeleton from "@/modules/common/components/ui/loadingSkeleton";
import PropertyStatusMobile from "./property-status";
import FollowUpMobile from "./follow-up-mobile";
import ClientDetails from "@/modules/property-tracks/components/sidebar/client-details";
import LeadStatusUpdate from "./track-lead-status";
import { PROPERTY_TRACKS } from "@/modules/property-tracks/utils";

function PropertyTrackMobile({ onError }: Readonly<{ onError?: () => void }>) {
  const { trackId } = useParams<{ trackId: string }>();

  const { data: track, isError: isTrackError } = useTrackQuery({
    uid: trackId,
  });

  const { data: journeyOldIdResponse } = useQuery({
    queryKey: ["getJourneyByOldId", track?.journey?.uid],
    queryFn: () => getJourneyByOldId({ id: track?.journey?.uid || "" }),
    enabled: !!track?.journey?.uid,
    staleTime: Infinity,
  });

  const {
    leadData,
    isLoading: isLeadLoading,
    stagesData,
    isStagesLoading,
    leadIsError,
  } = useLeadPageDataFetching(journeyOldIdResponse?.data.nanoId, false);

  const [sidebarModalOpen, setSidebarModalOpen] = useState(false);
  const theme = useTheme();

  if (leadIsError || isTrackError) {
    onError?.();
  }

  if (!track) {
    return <LoadingSkeleton />;
  }

  const isUnlockedFinishing = track.trackType?.name === PROPERTY_TRACKS.FINISHING;

  return (
    <LeadPageContext.Provider
      value={{
        lead: leadData,
        stages: stagesData,
        isStagesLoading: isStagesLoading,
      }}
    >
      <Stack width="100%" divider={<Divider sx={{ borderColor: "#DEE2E7" }} />}>
        <Stack
          direction="column"
          sx={{
            width: "100%",
            padding: theme.nawy.spacing.md,
            borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* Client details */}
          {isLeadLoading ? (
            <CustomerProfileSkeleton />
          ) : (
            <ClientDetails
              track={track}
              onPersonClick={() => setSidebarModalOpen(true)}
            />
          )}
        </Stack>

        <Box
          sx={(t) => ({
            px: t.nawy.spacing.sm,
            py: t.nawy.spacing.xs,
            backgroundColor: t.nawy.colors.primitives.white[200],
          })}
        >
          <PropertyStatusMobile track={track} />
        </Box>

        {isUnlockedFinishing && (
          <Box
            sx={(t) => ({
              px: t.nawy.spacing.sm,
              py: t.nawy.spacing.xs,
              backgroundColor: t.nawy.colors.primitives.white[200],
            })}
          >
            <FollowUpMobile track={track} />
          </Box>
        )}

        <Box
          sx={(t) => ({
            px: t.nawy.spacing.sm,
            py: t.nawy.spacing.xs,
            backgroundColor: t.nawy.colors.primitives.white[200],
          })}
        >
          <LeadStatusUpdate />
        </Box>

        {/* Tabs + content */}
        <PropertyTrackTabs track={track} />

        <CommonBottomSheet
          isOpen={sidebarModalOpen}
          onClose={() => setSidebarModalOpen(false)}
          snapPoints={[1]}
        >
          <LeadSidebar />
        </CommonBottomSheet>
      </Stack>
    </LeadPageContext.Provider>
  );
}

const PropertyTrackMobileWithErrorHandling =
  WithErrorHandling(PropertyTrackMobile);

export default PropertyTrackMobileWithErrorHandling;
